import { useContext, useEffect, useState } from 'react';
import { default as React } from 'react';
import { DataTable, DataTableValueArray } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  DevHubAlert,
  DevHubButton,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
} from '@devxdiscover/devhub-ui';
import { UniversalWorklistQuery } from './UniversalWorklistQuery';
import { useQuery } from '@apollo/client';
import { Progress } from '@backstage/core-components';
import { UserContext } from '../../contexts/UserContext';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import AssignmentIcon from '@material-ui/icons/Assignment';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import { useAnalytics } from '@backstage/core-plugin-api';

interface Application {
  applicationId: number;
  name: string;
}

interface Assignee {
  __typename: string;
  login: string;
  name: string;
  applications: Application[];
  role: { displayName: string };
}

interface AssignedTo {
  directs: JSX.Element[];
  roles: JSX.Element[][];
}

interface WorklistItem {
  actionUrl: string;
  assignedTo: AssignedTo;
  category: string;
  description: string;
  dueDate: string;
  id: string;
  name: string;
  priority: number;
  applicationId: number;
  source: string;
  status: string;
  supportUrl: string | null;
}

/**
 * Custom UniversalWorklistTable Component for DevHub
 * @constructor
 */
export const UniversalWorklistTable = () => {
  const { login } = useContext(UserContext);
  const analytics = useAnalytics();
  const [isRefetching, setIsRefetching] = React.useState(false);
  const uwlEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.UNIVERSAL_WORKLIST,
  );

  const { data, loading, refetch } = useQuery(UniversalWorklistQuery, {
    variables: { filter: { login: login, statuses: ['OPEN', 'IN_PROGRESS'] } },
  });

  const worklistItemData =
    data?.devhub?.universalWorklist.worklistItemsPaged?.worklistItems || [];

  const userDisplay = (user: Assignee) => {
    return (
      <DevHubChip
        icon={<PersonIcon />}
        label={user.login}
        size="small"
        variant="outlined"
      />
    );
  };

  const groupDisplay = (group: Assignee) => {
    return (
      <DevHubChip
        icon={<GroupIcon />}
        label={group.name}
        size="small"
        variant="outlined"
      />
    );
  };

  const appLink = (app: Application) => {
    return (
      <DevHubLink
        key={app.applicationId}
        href={`/catalog/default/system/${app.applicationId}`}
        target="_blank"
      >
        {app.name}
      </DevHubLink>
    );
  };

  const intersperse = (
    components: JSX.Element[],
    delimiter: string,
  ): JSX.Element[] => {
    return components.reduce((acc, component, index) => {
      if (index > 0)
        acc.push(<span key={`delimiter-${index}`}>{delimiter}</span>);
      acc.push(component);
      return acc;
    }, [] as JSX.Element[]);
  };

  const appsAsList = (apps: Application[]) => {
    const appLinks = apps.map(app => appLink(app));
    return intersperse(appLinks, ', ');
  };

  const roleDisplay: (assignee: Assignee) => JSX.Element[] = (
    assignee: Assignee,
  ) => {
    // const roleContent = <span key="role">{assignee.role.displayName} on </span>;
    const roleContent = (
      <DevHubChip
        icon={<AssignmentIcon />}
        label={assignee.role.displayName}
        size="small"
        variant="outlined"
      />
    );
    return [
      roleContent,
      <span>on </span>,
      ...appsAsList(assignee.applications),
    ];
  };

  const assignedTo = (assignees: [Assignee]) => {
    const result: AssignedTo = { directs: [], roles: [] };
    assignees.forEach((assignee: Assignee) => {
      switch (assignee.__typename) {
        case 'User':
          result.directs.push(userDisplay(assignee));
          break;
        case 'Group':
          result.directs.push(groupDisplay(assignee));
          break;
        case 'UwlWorklistRoleAssignment':
          result.roles.push(roleDisplay(assignee));
          break;
        default:
          break;
      }
    });
    return result;
  };

  const applicationIds = (assignees: Assignee[]): number[] => {
    return assignees.flatMap(assignee =>
      assignee.applications
        ? assignee.applications.map(app => app.applicationId)
        : [],
    );
  };

  const worklistItems: WorklistItem[] = worklistItemData.map(
    (item: any, index: number) => ({
      order: index + 1,
      actionUrl: item.actionUrl,
      assignedTo: assignedTo(item.assignedTo),
      category: item.category.name,
      description: item.description,
      dueDate: new Date(item.dueDate).toLocaleDateString(),
      id: item.id,
      name: item.name,
      priority: item.priority,
      applicationId: applicationIds(item.assignedTo)[0],
      source: item.source,
      status: item.status.replace(/_/g, ' '),
      supportUrl: item.supportUrl,
    }),
  );

  const [expandedRows, setExpandedRows] = useState<
    DataTableValueArray | undefined
  >(undefined);

  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setIsRefetching(false);
  };

  const onRowToggle = (e: { data: DataTableValueArray }) => {
    const selectedRow = e.data[0];
    if (selectedRow?.id) {
      analytics.captureEvent('security-uwl-row-toggle', selectedRow.id, {
        attributes: {
          appId: selectedRow.applicationId,
          uwlId: selectedRow.id,
        },
      });
    }
    setExpandedRows(e.data);
  };

  const rowExpansionTemplate = (rowData: WorklistItem) => {
    return (
      <DevHubGrid container>
        <DevHubGrid item xs={1} />
        <DevHubGrid container item xs={11} spacing={1}>
          {rowData.description && (
            <>
              <DevHubGrid item xs={2}>
                <span style={{ fontWeight: 'bold' }}>Description</span>
              </DevHubGrid>
              <DevHubGrid item xs={10}>
                {rowData.description}
              </DevHubGrid>
            </>
          )}
          {rowData.supportUrl && (
            <>
              <DevHubGrid item xs={2}>
                <span style={{ fontWeight: 'bold' }}>Support URL</span>
              </DevHubGrid>
              <DevHubGrid item xs={10}>
                <DevHubLink href={rowData.supportUrl} target="_blank">
                  {rowData.supportUrl}
                </DevHubLink>
              </DevHubGrid>
            </>
          )}
          {rowData.source && (
            <>
              <DevHubGrid item xs={2}>
                <span style={{ fontWeight: 'bold' }}>Source</span>
              </DevHubGrid>
              <DevHubGrid item xs={10}>
                {rowData.source}
              </DevHubGrid>
            </>
          )}
          {rowData.id && (
            <>
              <DevHubGrid item xs={2}>
                <span style={{ fontWeight: 'bold' }}>ID</span>
              </DevHubGrid>
              <DevHubGrid item xs={10}>
                {rowData.id}
              </DevHubGrid>
            </>
          )}
          {rowData && (
            <>
              <DevHubGrid item xs={2}>
                <span style={{ fontWeight: 'bold' }}>Assigned To</span>
              </DevHubGrid>
              <DevHubGrid item xs={10}>
                {rowData.assignedTo.directs.map(
                  (assignee: JSX.Element, index) => (
                    <DevHubGrid key={`assignee_${index}`} item xs={12}>
                      {assignee}
                    </DevHubGrid>
                  ),
                )}
                {rowData.assignedTo.roles.map((role: JSX.Element[], index) => (
                  <DevHubGrid key={`role_${index}`} item xs={12}>
                    {role}
                  </DevHubGrid>
                ))}
              </DevHubGrid>
            </>
          )}
        </DevHubGrid>
      </DevHubGrid>
    );
  };

  const onActionClick = (item: WorklistItem) => {
    analytics.captureEvent('security-uwl-take-action', item.id, {
      attributes: {
        appId: item.applicationId,
        uwlId: item.id,
      },
    });
    window.open(item.actionUrl, '_blank');
  };

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  return (
    <>
      {!uwlEnabled && (
        <DevHubAlert severity="info">
          Universal Worklist feature is not enabled.
        </DevHubAlert>
      )}

      {uwlEnabled && (
        <>
          {(loading || isRefetching) && <Progress />}
          {!loading && worklistItems.length === 0 && (
            <DevHubAlert severity="info">
              No worklist items available at the moment.
            </DevHubAlert>
          )}
          <DataTable
            value={worklistItems}
            expandedRows={expandedRows}
            onRowToggle={onRowToggle}
            rowExpansionTemplate={rowExpansionTemplate}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20]}
          >
            <Column expander style={{ width: '3em' }} />
            <Column
              field="order"
              header="Priority"
              bodyStyle={{ textAlign: 'center' }}
              style={{ width: '3em' }}
            />
            <Column field="applicationId" header="Application ID" />
            <Column field="status" header="Status" />
            <Column field="name" header="Worklist Item" />
            <Column field="category" header="Category" />
            <Column field="dueDate" header="Due Date" />
            <Column
              header={
                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleRefresh}
                >
                  {' '}
                  Refresh
                  <DevHubDynamicIcon iconName="Sync" />
                </DevHubButton>
              }
              body={(columnData: WorklistItem) =>
                columnData.actionUrl && (
                  <DevHubButton
                    variant="contained"
                    buttonColor="black"
                    onClick={() => onActionClick(columnData)}
                  >
                    Take Action
                  </DevHubButton>
                )
              }
            />
          </DataTable>
        </>
      )}
    </>
  );
};
